<template>
  <v-dialog v-model="dialog" max-width="800px">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Histórico da Atividade</span>

        <v-btn icon="mdi-close" variant="text" size="small" @click="close" aria-label="Fechar" />
      </v-card-title>

      <v-card-text>
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Carregando histórico...</p>
        </div>

        <div v-else-if="logs.length === 0" class="text-center pa-8">
          <v-icon size="64" color="grey-lighten-1">mdi-history</v-icon>
          <p class="mt-2 text-grey">Nenhum registro de histórico encontrado</p>
        </div>

        <v-timeline v-else align="start" class="mt-4">
          <v-timeline-item v-for="log in logs" :key="log.id" :dot-color="getLogColor(log.descricao)" size="small">
            <template #icon>
              <v-icon>{{ getLogIcon(log.descricao) }}</v-icon>
            </template>

            <div class="d-flex justify-space-between align-start">
              <div>
                <strong>{{ log.usuario_nome || 'Sistema' }}</strong>
                <span class="text-caption text-medium-emphasis ml-2">
                  {{ log.data_hora }}
                </span>
              </div>
            </div>

            <div class="mt-2">
              <div v-if="hasParsedDetails(log.descricao)">
                <p class="text-body-2 mb-2">{{ getSimpleDescription(log.descricao) }}</p>
                <div class="mt-1">
                  <div class="mb-2">
                    <span class="text-caption font-weight-medium">De:</span>
                    <v-card class="pa-1 mt-1" variant="outlined"
                      :color="getDetailColor(extractChangeDetails(log.descricao).de)"
                      style="white-space: normal; min-height: auto; height: auto; font-size: 0.7rem;">
                      <span style="line-height: 1.3; word-break: break-word; font-size: 0.7rem;">{{
                        extractChangeDetails(log.descricao).de }}</span>
                    </v-card>
                  </div>
                  <div>
                    <span class="text-caption font-weight-medium">Para:</span>
                    <v-card class="pa-1 mt-1" variant="outlined"
                      :color="getDetailColor(extractChangeDetails(log.descricao).para)"
                      style="white-space: normal; min-height: auto; height: auto; font-size: 0.7rem;">
                      <span style="line-height: 1.3; word-break: break-word; font-size: 0.7rem;">{{
                        extractChangeDetails(log.descricao).para }}</span>
                    </v-card>
                  </div>
                </div>
              </div>

              <div v-else>
                <p class="text-body-2 mb-1">{{ log.descricao }}</p>
              </div>
            </div>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="close">Fechar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import api from '@/services/api'
import { useToastStore } from '@/stores/toast'

const toast = useToastStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  activityId: {
    type: [String, Number],
    default: null
  },
  isSubtask: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const dialog = ref(false)
const logs = ref([])
const loading = ref(false)

const getLogColor = (descricao) => {
  if (descricao.includes('status') && descricao.includes('Em Andamento')) return 'warning'
  if (descricao.includes('status') && descricao.includes('Concluída')) return 'success'
  if (descricao.includes('status') && descricao.includes('Não Iniciada')) return 'grey'
  if (descricao.includes('criou') || descricao.includes('criada')) return 'success'
  if (descricao.includes('excluiu') || descricao.includes('excluída')) return 'error'
  if (descricao.includes('alterado') || descricao.includes('atualizou')) return 'info'
  return 'primary'
}

const getLogIcon = (descricao) => {
  if (descricao.includes('status')) return 'mdi-sync'
  if (descricao.includes('criou') || descricao.includes('criada')) return 'mdi-plus'
  if (descricao.includes('excluiu') || descricao.includes('excluída')) return 'mdi-delete'
  if (descricao.includes('alterado') || descricao.includes('atualizou')) return 'mdi-pencil'
  return 'mdi-history'
}

const hasParsedDetails = (descricao) => {
  return descricao.includes("Campo '") && descricao.includes("alterado de")
}

const getSimpleDescription = (descricao) => {
  if (descricao.includes("Campo 'status'")) {
    return "Alteração no campo \"Status\""
  }
  if (descricao.includes("Campo '")) {
    const campoMatch = descricao.match(/Campo '([^']+)'/)
    if (campoMatch) {
      const campo = campoMatch[1]
      const camposTraduzidos = {
        'status': 'Status',
        'titulo': 'Título',
        'descricao': 'Descrição',
        'data_prazo': 'Data de vencimento',
        'priority': 'Prioridade'
      }
      return `Alteração no campo "${camposTraduzidos[campo] || campo}"`
    }
  }
  return descricao
}

const extractChangeDetails = (descricao) => {
  const details = {
    de: '',
    para: ''
  }

  const statusMatch = descricao.match(/Campo '([^']+)' alterado de '([^']+)' para '([^']+)'/)
  if (statusMatch) {
    const [, campo, de, para] = statusMatch

    const formatValor = (valor) => {
      const traducoes = {
        'Não Iniciada': 'Não Iniciada',
        'Em Andamento': 'Em Andamento',
        'Concluída': 'Concluída',
        'low': 'Baixa',
        'medium': 'Média',
        'high': 'Alta'
      }
      return traducoes[valor] || valor
    }

    details.de = formatValor(de)
    details.para = formatValor(para)
  }

  return details
}

const getDetailColor = (detail) => {
  if (detail.includes('Em Andamento')) return 'warning'
  if (detail.includes('Concluída')) return 'success'
  if (detail.includes('Não Iniciada')) return 'grey'
  if (detail.includes('Alta')) return 'error'
  if (detail.includes('Média')) return 'warning'
  if (detail.includes('Baixa')) return 'success'
  return 'primary'
}

const fetchLogs = async () => {
  if (!props.activityId) {
    logs.value = []
    return
  }

  try {
    loading.value = true
    const response = await api.get(`logs/${props.activityId}`)

    if (response.data && response.data.data) {
      logs.value = response.data.data
    } else {
      logs.value = []
    }

  } catch (error) {
    console.error('Erro ao carregar histórico:', error)
    toast.error('Erro ao carregar histórico')
    logs.value = []
  } finally {
    loading.value = false
  }
}

const close = () => {
  emit('update:modelValue', false)
}

watch(() => props.modelValue, (newVal) => {
  dialog.value = newVal
  if (newVal && props.activityId) {
    fetchLogs()
  }
})

watch(() => props.activityId, (newVal) => {
  if (dialog.value && newVal) {
    fetchLogs()
  }
})

watch(dialog, (newVal) => {
  if (newVal !== props.modelValue) {
    emit('update:modelValue', newVal)
  }
})
</script>

<style scoped>
.v-timeline-item {
  min-height: 80px;
}

.v-timeline-item::before {
  content: '';
  position: absolute;
  left: -20px;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: rgb(var(--v-theme-outline-variant));
}

.detail-chip-wrapper {
  display: inline-block;
  width: 100%;
  margin-right: 8px;
  margin-bottom: 4px;
}

.v-chip {
  font-size: 0.7rem;
  min-height: auto;
  height: auto;
  padding: 4px 8px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
}

.text-body-2 {
  line-height: 1.4;
}

.gap-2 {
  gap: 8px;
}
</style>
